import React, { useEffect, useState } from "react";
import API from "../services/api";

const OngoingBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Details Modal States
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [bids, setBids] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);

  const fetchOngoingBookings = async () => {
    try {
      setLoading(true);
      const res = await API.get("/ongoing-bookings");
      if (res.data.success) {
        setBookings(res.data.bookings);
      } else {
        setError("Failed to fetch bookings");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = async (bookingId) => {
    try {
      setDetailLoading(true);
      const res = await API.get(`/booking/${bookingId}`);
      if (res.data.success) {
        setSelectedBooking(res.data.booking);
        setBids(res.data.bids);
        setIsModalOpen(true);
      }
    } catch (err) {
      alert("Error loading live ride details");
    } finally {
      setDetailLoading(false);
    }
  };

  useEffect(() => {
    fetchOngoingBookings();
  }, []);

  if (loading) return <div style={styles.loading}>Loading ongoing bookings...</div>;
  if (error) return <div style={styles.error}>{error}</div>;

  return (
    <div style={styles.page}>
      {/* HEADER */}
      <div style={styles.header}>
        <div>
          <h2 style={styles.title}>Ongoing Bookings</h2>
          <p style={styles.subtitle}>Monitor active rides and real-time status</p>
        </div>
        <div style={styles.badgeCount}>{bookings.length} Active</div>
      </div>

      {/* GRID */}
      {bookings.length === 0 ? (
        <div style={styles.empty}>No ongoing bookings at the moment</div>
      ) : (
        <div style={styles.grid}>
          {bookings.map((b) => (
            <div 
              key={b._id} 
              style={styles.card} 
              onClick={() => handleViewDetails(b._id)}
            >
              <div style={styles.cardTop}>
                <span style={styles.bookingId}>{b._id.slice(-6).toUpperCase()}</span>
                <span style={styles.statusBadge}>{b.status}</span>
              </div>

              <div style={styles.row}>
                <span style={styles.label}>Passenger</span>
                <span style={styles.value}>{b.user?.name || "N/A"}</span>
              </div>

              <div style={styles.row}>
                <span style={styles.label}>Driver</span>
                <span style={styles.value}>{b.assignedDriver?.name || "Assigning..."}</span>
              </div>

              <div style={styles.footer}>
                Started: {new Date(b.createdAt).toLocaleString()}
                {detailLoading && " • Updating..."}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* LIVE MONITOR MODAL */}
      {isModalOpen && selectedBooking && (
        <div style={styles.overlay} onClick={() => setIsModalOpen(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div style={styles.modalHeaderTitle}>
                <div style={styles.pulse}></div>
                <h3>Live Track: {selectedBooking._id.slice(-6).toUpperCase()}</h3>
              </div>
              <button style={styles.closeBtn} onClick={() => setIsModalOpen(false)}>×</button>
            </div>

            <div style={styles.modalBody}>
              {/* STATUS TRACKER */}
              <div style={styles.statusSection}>
                <p style={styles.modalLabel}>Current Status</p>
                <div style={styles.statusMain}>{selectedBooking.status}</div>
              </div>

              {/* DRIVER & VEHICLE */}
              <div style={styles.detailGrid}>
                <div style={styles.detailItem}>
                  <p style={styles.modalLabel}>Driver Information</p>
                  <p style={styles.modalVal}>{selectedBooking.assignedDriver?.name || "Searching..."}</p>
                  <p style={styles.modalSub}>{selectedBooking.assignedDriver?.phone}</p>
                </div>
                <div style={styles.detailItem}>
                  <p style={styles.modalLabel}>Vehicle Details</p>
                  <p style={styles.modalVal}>{selectedBooking.assignedDriver?.vehicleDetails?.model || "N/A"}</p>
                  <p style={styles.modalSub}>{selectedBooking.assignedDriver?.vehicleDetails?.number}</p>
                </div>
              </div>

              {/* RECENT BIDS */}
              <h4 style={styles.bidTitle}>Auction History (Bids)</h4>
              <div style={styles.bidContainer}>
                {bids.length === 0 ? (
                  <p style={styles.emptyBids}>No other bids recorded for this ride.</p>
                ) : (
                  bids.map((bid) => (
                    <div key={bid._id} style={styles.bidRow}>
                      <span>{bid.driver?.name}</span>
                      <span style={styles.bidAmt}>₹{bid.amount}</span>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ================= STYLES ================= */

const styles = {
  // Existing Styles
  page: { padding: "20px", background: "#f5f7fb", minHeight: "100vh", fontFamily: "'Segoe UI', Tahoma, sans-serif" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "25px" },
  title: { margin: 0, fontSize: "24px", color: "#1f2937" },
  subtitle: { margin: "4px 0 0 0", fontSize: "13px", color: "#6b7280" },
  badgeCount: { background: "#3b82f6", color: "white", padding: "8px 14px", borderRadius: "20px", fontSize: "13px", fontWeight: "bold" },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: "20px" },
  card: { background: "#fff", borderRadius: "14px", padding: "18px", boxShadow: "0 4px 12px rgba(0,0,0,0.05)", border: "1px solid #e5e7eb", cursor: "pointer" },
  cardTop: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "15px" },
  bookingId: { fontFamily: "monospace", fontSize: "12px", fontWeight: "bold", color: "#9ca3af", background: "#f3f4f6", padding: "2px 6px", borderRadius: "4px" },
  statusBadge: { background: "#fef3c7", color: "#d97706", padding: "4px 10px", borderRadius: "20px", fontSize: "11px", fontWeight: "bold", textTransform: "uppercase" },
  row: { display: "flex", justifyContent: "space-between", marginBottom: "10px" },
  label: { fontSize: "12px", color: "#6b7280", fontWeight: "500" },
  value: { fontSize: "13px", fontWeight: "600", color: "#111827" },
  footer: { marginTop: "12px", fontSize: "11px", color: "#9ca3af", borderTop: "1px solid #f3f4f6", paddingTop: "10px" },
  loading: { padding: "40px", textAlign: "center", color: "#3b82f6", fontSize: "18px" },
  error: { color: "#dc2626", padding: "20px", textAlign: "center", background: "#fee2e2", margin: "20px", borderRadius: "8px" },
  empty: { textAlign: "center", padding: "60px", color: "#9ca3af", background: "#fff", borderRadius: "14px" },

  // Modal Styles
  overlay: { position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.5)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 1000, backdropFilter: "blur(2px)" },
  modal: { background: "#fff", width: "90%", maxWidth: "550px", borderRadius: "20px", overflow: "hidden", boxShadow: "0 20px 25px -5px rgba(0,0,0,0.1)" },
  modalHeader: { padding: "20px", borderBottom: "1px solid #eee", display: "flex", justifyContent: "space-between", alignItems: "center" },
  modalHeaderTitle: { display: "flex", alignItems: "center", gap: "10px" },
  pulse: { width: "10px", height: "10px", background: "#3b82f6", borderRadius: "50%", boxShadow: "0 0 0 rgba(59, 130, 246, 0.4)", animation: "pulse 2s infinite" },
  closeBtn: { background: "none", border: "none", fontSize: "28px", cursor: "pointer", color: "#9ca3af" },
  modalBody: { padding: "20px" },
  statusSection: { textAlign: "center", background: "#f0f7ff", padding: "15px", borderRadius: "12px", marginBottom: "20px" },
  statusMain: { fontSize: "20px", fontWeight: "800", color: "#1e40af", textTransform: "uppercase" },
  modalLabel: { fontSize: "11px", color: "#6b7280", textTransform: "uppercase", marginBottom: "4px" },
  detailGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px", marginBottom: "25px" },
  modalVal: { fontSize: "14px", fontWeight: "700", color: "#111827", margin: 0 },
  modalSub: { fontSize: "12px", color: "#6b7280", margin: 0 },
  bidTitle: { fontSize: "14px", fontWeight: "700", marginBottom: "12px" },
  bidContainer: { border: "1px solid #f3f4f6", borderRadius: "12px" },
  bidRow: { display: "flex", justifyContent: "space-between", padding: "12px 15px", borderBottom: "1px solid #f3f4f6", fontSize: "13px" },
  bidAmt: { fontWeight: "bold", color: "#3b82f6" },
  emptyBids: { textAlign: "center", padding: "15px", fontSize: "12px", color: "#9ca3af" }
};

export default OngoingBookings;